import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { usePanelModify } from '../../../modules/panel';
import { useDragHandler } from '../../../utils/useDragHandler';
import { updatePanelTitles } from '../../../modules/wiring';
import { selectPanelTypes, selectOutputTypes, selectOutputCategories } from '../../../redux/panelConfigSlice';
import { getPanelMenuItems, mapOutputTypeToCommand, resolvePanelTypeId } from '../../../utils/panelMenuBuilder';
import { getChildPanelTypes } from '../../../config/panelConfig/panelHierarchy';
import { selectAllFloorPlans } from '../../../redux/floorPlanSlice';
import { updateComponent } from '../../../redux/simpleSupplySlice';
import { buildDefaultPanelName } from '../../../utils/panelNaming';
import PanelTypeMenuItem from './PanelTypeMenuItem';
import PanelOutputFlyout from './PanelOutputFlyout';
import PanelSettingsPopup from './PanelSettingsPopup';
// eslint-disable-next-line no-restricted-imports -- CSS paylaşımlı, components/TypeMenus/'ta
import '../../../components/TypeMenus/PanelTypeMenus.css';
import './PanelTypeMenu.css';
import { getObjectById } from '../../../utils/getObjectById';

// Başa/Sona Ekle seçimi kalıcı: menü her açıldığında sıfırlanmaz, son seçim korunur
// (localStorage → oturumlar arası da kalıcı). İlk kullanımda default 'end' (Sona Ekle) —
// kullanıcı isteği: default başa ekle OLMASIN.
const ADD_POSITION_STORAGE_KEY = 'panelTypeMenu.addPosition';
function getInitialAddPosition() {
  try {
    const v = localStorage.getItem(ADD_POSITION_STORAGE_KEY);
    if (v === 'start' || v === 'end') return v;
  } catch (_) { /* localStorage erişilemezse default'a düş */ }
  return 'end';
}

export default function PanelTypeMenu({
  x,
  y,
  value,
  onClose,
  onAddPositionChange,
  onActionSelect,
  panelDetails,
  onDrag,
  scene,
}) {
  const [addPosition, setAddPosition] = useState(getInitialAddPosition);
  const [showDetails, setShowDetails] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [panel, setPanel] = useState(null);
  const [details, setDetails] = useState({
    purposeOfUse: 'Mesken',
    subscribeType: 'Mesken',
    installedPower: 0,
    demandFactor: '60-40%',
    demandPower: 0,
    phaseCount: 3,
    current: 0,
  });
  const [salt, setSalt] = useState();
  const menuRef = useRef(null);
  const { handleMouseDown } = useDragHandler(onDrag);

  const dispatch = useDispatch();
  const panelId = panelDetails.id;
  const simpleSupply = useSelector((state) => state.simpleSupply);
  const floorPlans = useSelector(selectAllFloorPlans);

  // Redux panel config
  const panelTypes = useSelector(selectPanelTypes);
  const outputTypes = useSelector(selectOutputTypes);
  const outputCategories = useSelector(selectOutputCategories);

  // Find electrical panel component from Simple SupplyPoint
  const panelComponent = useMemo(() => {
    return simpleSupply.components.find(c => c.id === panelId) || {
      name: 'Electrical Panel',
      electrical: {
        voltage: 380,
        phaseCount: 3,
        current: 25,
        power: 5000,
        purposeOfUse: panelDetails.details?.kullanimAmac || 'Mesken',
        subscribeType: panelDetails.details?.aboneTipi || 'Mesken',
        installedPower: 0,
        demandFactor: '60-40%',
        demandPower: 0,
      }
    };
  }, [simpleSupply.components, panelId, panelDetails.details?.kullanimAmac, panelDetails.details?.aboneTipi]);

  const [panelName, setPanelName] = useState(panelComponent?.name || 'Electrical Panel');
  const { setCount, updatePanel } = usePanelModify(scene);

  // Resolve panel type from kullanimAmac (panelTypes geçerek tüm tipler çözülenebilir)
  const kullanimAmac = details.purposeOfUse || panelDetails.details?.kullanimAmac || 'Mesken';
  const panelTypeId = resolvePanelTypeId(kullanimAmac, panelTypes);
  const resolvedPanelType = panelTypes[panelTypeId];

  // Build dynamic menu items
  const { outputGroups, panelOutputs } = getPanelMenuItems(
    panelTypeId, panelTypes, outputTypes, outputCategories
  );

  // Get child panel types for flyout
  const childPanelTypes = getChildPanelTypes(panelTypeId, panelTypes);

  useEffect(() => {
    if (!scene || !panelId) return;

    let foundPanel = null;
    scene.traverse(obj => {
      if (obj.userData.id === panelId) {
        foundPanel = obj.userData;
      }
    });
    if (!foundPanel) return;

    if (foundPanel && foundPanel.name) {
      setPanelName(foundPanel.name);
      setPanel(foundPanel);
    }

    if (foundPanel.details?.kullanimAmac) {
      setDetails(prev => ({
        ...prev,
        purposeOfUse: foundPanel.details.kullanimAmac,
        subscribeType: foundPanel.details.aboneTipi || prev.subscribeType,
      }));
    }
  }, [scene, panelId]);

  useEffect(() => {
    if (panelComponent && panelComponent.electrical) {
      setDetails(panelComponent.electrical);
      setSalt({
        cable: { title: "NYY 3x2.5+1x1.5", currentCap: 25, housing: "boru" },
        AOS: { title: "25A B Tipi Otomatik Sigorta" },
        KAKR: { title: "25A Kaçak Akım Rölesi" }
      });
    }
  }, [simpleSupply.components, panelComponent]);

  useEffect(() => {
    if (!panel) return;
  }, [panel]);

  // Default ad: kat türü + numara + kattaki sıra (örn. "1-N.K.T.-1").
  // NOT: Hook'lar early-return'den ÖNCE çağrılmalı (rules-of-hooks).
  const defaultPanelName = useMemo(
    () => buildDefaultPanelName({
      position: panelComponent?.position,
      floorPlans,
      allComponents: simpleSupply.components,
      componentId: panelComponent?.id,
    }),
    [panelComponent, floorPlans, simpleSupply.components]
  );

  if (!panelComponent) {
    return <div>Panel bilgileri yükleniyor...</div>;
  }

  const monoPhaseLimit = 10000;

  // Local input değişikliklerini Redux + scene userData'ya yansıt
  const persistPanelName = (name) => {
    if (!panelId) return;
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    dispatch(updateComponent({ id: panelId, changes: { name: trimmed } }));
    if (scene) {
      scene.traverse(obj => {
        if (obj.userData?.id === panelId) {
          obj.userData.name = trimmed;
        }
      });
      // Görünür title'ı yenile
      try { updatePanelTitles(scene, panelId, true); } catch (_) {}
    }
  };

  const changePanelName = (name) => {
    setPanelName(name);
  };

  const handlePanelNameCommit = () => {
    persistPanelName(panelName);
  };

  const handleResetPanelName = () => {
    if (!defaultPanelName) return;
    setPanelName(defaultPanelName);
    persistPanelName(defaultPanelName);
  };

  const changePhaseCount = (count) => {
    setDetails(prev => ({ ...prev, phaseCount: count }));
  };

  const handlePositionChange = (e) => {
    const val = e.target.value;
    setAddPosition(val);
    // Seçimi kalıcı sakla — menü tekrar açıldığında son seçim gelsin.
    try { localStorage.setItem(ADD_POSITION_STORAGE_KEY, val); } catch (_) { /* yok say */ }
    onAddPositionChange(val);
  };

  // Generic action handler from output type
  const handleOutputAction = (outputType) => {
    const mapping = mapOutputTypeToCommand(outputType.id);
    if (!mapping) return;

    const panelObj = getObjectById(scene, panelId);
    if (!panelObj) return;

    const currentOutCount = panelObj.userData.outs?.length || 0;
    const newCount = currentOutCount + 1;

    setCount(panelId, "nodeId", "housingId", newCount, {
      addPosition: addPosition,
      command: mapping.command,
      initialItemType: mapping.initialItemType,
    });
    onClose();
  };

  // Panel add handler (for flyout child panels)
  const handleAddPanel = (childPanelType) => {
    const panelObj = getObjectById(scene, panelId);
    if (!panelObj) return;

    const currentOutCount = panelObj.userData.outs?.length || 0;
    const newCount = currentOutCount + 1;

    // PNL_ prefix'li output type'ı bul
    const pnlOutputId = `PNL_${childPanelType.id}`;
    const mapping = mapOutputTypeToCommand(pnlOutputId);
    if (!mapping) return;

    setCount(panelId, "nodeId", "housingId", newCount, {
      addPosition: addPosition,
      command: mapping.command,
      initialItemType: mapping.initialItemType,
      name: childPanelType.label,
      purposeOfUse: childPanelType.label,
      aboneTuru: childPanelType.label,
    });
    onClose();
  };

  const handleAboneTuru = (val) => {
    const subscribeType = val.target.value;
    setPanel(prev => ({
      ...prev,
      details: {
        ...prev.details,
        subscribeType: subscribeType,
      },
    }));
  };

  const handleKullanimAmac = (val) => {
    const purposeOfUse = val.target.value;
    setPanel(prev => ({
      ...prev,
      details: {
        ...prev.details,
        purposeOfUse: purposeOfUse,
      },
    }));
  };

  const handleMouseDownWithStop = (e) => {
    e.stopPropagation();
    handleMouseDown(e);
  };

  return (
    <div
      ref={menuRef}
      className="panel-type-menu"
      style={{ left: x, top: y }}
    >
      {/* Header */}
      <div
        onMouseDown={handleMouseDownWithStop}
        className="panel-type-menu-header"
      >
        {panelName}/{resolvedPanelType?.code || details.purposeOfUse}/{details.installedPower} W
        <button
          className="close-button"
          onClick={e => { e.stopPropagation(); onClose(); }}
        >
          &times;
        </button>
      </div>

      {/* Panel Adı satırı — header'ın hemen altı. Default: kat türü + numara
          + kattaki sıra (örn. "1-N.K.T.-1"). Reset butonu ↻ default'a döndürür. */}
      <div className="panel-name-row">
        <input
          type="text"
          className="panel-name-input"
          value={panelName}
          placeholder={defaultPanelName || 'Panel Adı'}
          onChange={(e) => changePanelName(e.target.value)}
          onBlur={handlePanelNameCommit}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
        />
        <button
          type="button"
          className="panel-name-reset-btn"
          onClick={handleResetPanelName}
          title={defaultPanelName ? `Varsayılana dön: ${defaultPanelName}` : 'Varsayılan ad belirlenemedi'}
          disabled={!defaultPanelName}
        >
          &#8634;
        </button>
      </div>

      <div className="panel-type-options">
        {/* Radio Group: Başa/Sona Ekle */}
        <div className="panel-type-radio-group">
          <label style={{ fontSize: '14px' }}>
            <input
              type="radio"
              name="addPosition"
              value="start"
              checked={addPosition === 'start'}
              onChange={handlePositionChange}
              style={{ marginRight: '4px' }}
            />
            Başa Ekle
          </label>
          <label style={{ fontSize: '14px' }}>
            <input
              type="radio"
              name="addPosition"
              value="end"
              checked={addPosition === 'end'}
              onChange={handlePositionChange}
              style={{ marginRight: '4px' }}
            />
            Sona Ekle
          </label>
        </div>

        {/* Dynamic Output Groups */}
        <div className="panel-type-buttons">
          {outputGroups.map((group) => {
            const { category, items } = group;

            // Single item in category - render directly
            if (items.length === 1) {
              return (
                <PanelTypeMenuItem
                  key={items[0].id}
                  outputType={items[0]}
                  onAdd={handleOutputAction}
                />
              );
            }

            // Multiple items - use flyout submenu
            return (
              <CategoryFlyout
                key={category.id}
                category={category}
                items={items}
                onAdd={handleOutputAction}
              />
            );
          })}

          {/* Panel Output Flyout */}
          {childPanelTypes.length > 0 && (
            <PanelOutputFlyout
              childPanelTypes={childPanelTypes}
              panelTypeId={panelTypeId}
              onAddPanel={handleAddPanel}
            />
          )}

          {/* Etiket Güncelle */}
          <button
            onClick={() => updatePanelTitles(scene, panelId, true)}
            className="my-button"
          >
            Etiket Güncelle
          </button>
        </div>

        <hr />

        {/* Details Section */}
        <div>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="my-button"
            style={{ width: '100%', textAlign: 'left' }}
          >
            Detay {showDetails ? '\u25B4' : '\u25BE'}
          </button>

          {showDetails && (
            <div className="panel-type-details-box">
              <table className="panel-type-details-table">
                <tbody>
                  <tr>
                    <td className="label-cell">Pano Adı</td>
                    <td>
                      <input
                        type="text"
                        value={panelName}
                        onChange={(e) => changePanelName(e.target.value)}
                        style={{ width: '100%' }}
                        className="textArea"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="label-cell">Abone Türü</td>
                    <td>
                      <select
                        style={{ width: '100%', maxWidth: '160px' }}
                        onChange={e => handleAboneTuru(e)}
                        value={details.subscribeType}
                      >
                        <option value="Mesken">Mesken</option>
                        <option value="Ticari">Ticari</option>
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td className="label-cell">Kullanım Amacı</td>
                    <td>
                      <select
                        style={{ width: '100%', maxWidth: '160px' }}
                        value={details.purposeOfUse}
                        onChange={e => handleKullanimAmac(e)}
                      >
                        {details.subscribeType === 'Mesken' && (
                          <option value="Mesken">Mesken</option>
                        )}
                        {details.subscribeType === 'Ticari' && (
                          <option value="İşYeri">İş Yeri</option>
                        )}
                        <option value="Müşterek">Müşterek</option>
                        <option value="Asansör">Asansör</option>
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td className="label-cell">Kurulu G.</td>
                    <td>{details.installedPower}W</td>
                  </tr>
                  <tr>
                    <td className="label-cell">Talep Faktör</td>
                    <td>{details.demandFactor}</td>
                  </tr>
                  <tr>
                    <td className="label-cell">Talep Güç</td>
                    <td>{details?.demandPower?.toFixed(2)}W</td>
                  </tr>
                  <tr>
                    <td className="label-cell">Faz</td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <label title={details.installedPower > monoPhaseLimit ? `Pano gücü ${monoPhaseLimit / 1000} kW dan büyük olduğu için 1 faz seçilemez` : ''}>
                          <input
                            disabled={details.installedPower > monoPhaseLimit}
                            type="radio"
                            name="panelFaz"
                            checked={details.phaseCount === 1}
                            onChange={() => changePhaseCount(1)}
                          />
                          1 Faz
                        </label>
                        <label>
                          <input
                            type="radio"
                            name="panelFaz"
                            checked={details.phaseCount === 3}
                            onChange={() => changePhaseCount(3)}
                          />
                          3 Faz
                        </label>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="label-cell">Akım</td>
                    <td>{details.current.toFixed(2)}A</td>
                  </tr>
                  {salt && (
                    <>
                      <tr>
                        <td className="label-cell">Kolon</td>
                        <td>{salt.cable.title}</td>
                      </tr>
                      <tr>
                        <td className="label-cell">Akım T.Kap.</td>
                        <td>
                          {salt.cable.currentCap}A
                          {salt.cable.housing === "earth" ? " (Toprak)" : (salt.cable.housing === "air" ? " (Hava)" : " (Boru)")}
                        </td>
                      </tr>
                      <tr>
                        <td className="label-cell">Kesici</td>
                        <td>{salt.AOS.title}</td>
                      </tr>
                      <tr>
                        <td className="label-cell">Yangın K.Ş.</td>
                        <td>{salt.KAKR.title}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Settings Button */}
        <button
          className="settings-trigger-btn"
          onClick={() => setShowSettings(true)}
        >
          Ayarlar
        </button>
      </div>

      {/* Settings Popup */}
      {showSettings && (
        <PanelSettingsPopup onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
}

/**
 * CategoryFlyout - Birden fazla öğesi olan kategoriler için hover flyout
 */
function CategoryFlyout({ category, items, onAdd }) {
  const [showSubmenu, setShowSubmenu] = useState(false);

  return (
    <div
      className="flyout-container"
      onMouseEnter={() => setShowSubmenu(true)}
      onMouseLeave={() => setShowSubmenu(false)}
    >
      <button
        className="my-button flyout-trigger"
      >
        {category.label} <span className="flyout-arrow">&#9658;</span>
      </button>
      {showSubmenu && (
        <div className="flyout-menu">
          {items.map((item) => (
            <button
              key={item.id}
              onClick={() => onAdd(item)}
              className="my-button"
              style={{ textAlign: 'left', fontSize: '12px' }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
